import {Injectable} from '@angular/core';
import {TranslateService, TranslationObject} from '@ngx-translate/core';

interface Translation {
    language: string;
    translations: TranslationObject;
}

@Injectable({
    providedIn: 'root'
})
export class TranslationLoaderService {

    private translations: Translation[] = [
        {
            language: 'de',
            translations: {
                'profileLearnerMissing': {
                    'title': 'Profil noch nicht bereit',
                    'description': 'Dein Profil wurde noch nicht gefunden. Starte zuerst einen Chat in dieser App und versuche es dann erneut.',
                    'retry': 'Erneut versuchen'
                },
                'resourceViewer': {
                    'notAvailableError': {
                        'title': 'Ressource nicht verfügbar',
                        'description': 'Die angefragte Ressource ist leider nicht verfügbar.'
                    }
                },
                'badge': {
                    'locked': 'Unerreicht',
                    'level': 'Level',
                    'noLevel': 'Erreicht',
                    'noData': 'Die Badgeübersicht ist für diese Organisation deaktiviert'
                },
                'learningGoal': {
                    'newbie': 'Neuling',
                    'beginners': 'Anfänger',
                    'advanced': 'Fortgeschrittener',
                    'skilled': 'Könner',
                    'professional': 'Profi',
                    'expert': 'Experte',
                    'noData': 'Keine freigeschalteten Lernpakete',
                    'noIntroduction': 'Keine Beschreibung vorhanden',
                    'feedback': 'Feedback geben',
                    'releaseDate': 'Letzte Aktualisierung:',
                    'ownerOrganization': 'Erstellt von',
                    'checkSticker': 'Lernziel erreicht',
                    'forgettingApplied': 'Zeit zum Lernen',
                    'navLearningProgress': 'Lernziel- und Fortschritt',
                    'navQuizzesOverview': 'Visualisierung des Lernfortschritts',
                    'navLearningPackageInfo': {
                        'learningPackage': 'Lernpaket Insights',
                        'managedTutor': 'Tutor Insights'
                    },
                    'learningGoal': 'Lernziel',
                    'numberOfQuestion': 'Fortschritt der {{numberOfQuestion}} Fragen',
                    'unansweredQuestions': 'davon {{neverAnsweredQuestions}} unbeantwortet',
                    'unansweredAndInactiveQuestions': 'davon {{neverAnsweredQuestions}} unbeantwortet und {{numberOfInactiveQuestions}} inaktiv',
                    'inactiveQuestions': 'davon {{numberOfInactiveQuestions}} inaktiv',
                    'learnButton': {
                        'learningPackage': 'Dieses Lernpaket lernen',
                        'managedTutor': 'Diesen Tutor starten'
                    },
                    'inactive': 'inaktiv',
                    'learningHistoryCopyToast': 'Lernhistorie kopiert',
                    'insightsPopup': {
                        'learningProgress': 'Lernfortschritt',
                        'forgetting': 'Vergessen',
                        'details': {
                            'tabTitle': 'Details',
                            'noIntroduction': 'Keine Beschreibung vorhanden',
                            'releaseDate': 'Letzte Aktualisierung',
                            'feedback': 'Feedback einreichen',
                            'buttonTitle': 'Feedback geben',
                            'learning': {
                                'learningPackage': 'Lernpaket lernen',
                                'managedTutor': 'Tutor starten'
                            },
                            'learningTitle': 'Jetzt lernen',
                            'availableLanguages': 'Verfügbare Sprachen'
                        },
                        'feedback': {
                            'tabTitle': 'Feedback',
                            'clearCurrentFeedbackButton': 'Feedback verwerfen',
                            'startNewFeedbackButton': 'Neues Feedback starten'
                        },
                        'questions': {
                            'tabTitle': 'Fragenübersicht',
                            'deactivatedQuestionLegend': 'Inaktive Frage',
                            'notAnsweredQuestionLegend': 'Unbeantwortete Frage',
                            'numberOfQuestions': 'Dieses Lernpaket <strong>enthält {{numberOfQuestion}} Fragen</strong>',
                            'allQuestionsAnswered': ', die du bereits <strong>alle mindestens einmal beantwortet</strong> hast. ',
                            'noAnsweredQuestions': '. Davon hast du noch <strong>keine Fragen beantwortet</strong>. ',
                            'notShowedYetQuestions': '. Es wurden dir <strong>{{neverAnsweredQuestions}} Fragen noch nicht angezeigt</strong>. ',
                            'inactiveQuestions': 'Ausserdem sind <strong>{{numberOfInactiveQuestions}} Fragen inaktiv</strong>. ',
                            'allInactiveQuestions': 'Davon sind <strong>alle inaktiv</strong>. ',
                            'colorExplanation': 'Grüne Fragen beantwortest du nachhaltig korrekt. Bei roten Fragen gibst du noch falsche Antworten.',
                            'helpButtonTitle': 'Erklärung des Diagramms'
                        },
                        'topics': {
                            'tabTitle': 'Themen',
                            'noData': 'Starte eine Konversation mit dem Tutor, um deinen Fortschritt zu sehen!',
                            'colorExplanation': 'Die Farbe jedes Quadrats zeigt deinen Wissensstand für dieses Thema.',
                            'masteryLegend': 'Wissenstand'
                        },
                        'learningProgressHistory': {
                            'tabTitle': 'Lernfortschritt',
                            'correctAnswersLabel': 'Korrekte Antworten',
                            'incorrectAnswersLabel': 'Falsche Antworten',
                            'answeredQuestions': 'Beantwortete Fragen',
                            'dayLabel': 'Tage',
                            'weekLabel': 'Wochen',
                            'monthLabel': 'Monate',
                            'learningProgressLabel': 'Lernfortschritt',
                            'learningProgressTooltip': 'Lernfortschritt in %',
                            'weekShortVersion': 'KW',
                            'learningGoalLabel': 'Lernziel',
                            'forgettingLabel': 'Vergessen',
                            'timeOfForgettingLabel': 'Zeitpunkt des Vergessens',
                            'nowLabel': 'Jetzt',
                            'dailyMode': 'Tag',
                            'weeklyMode': 'Woche',
                            'monthlyMode': 'Monat',
                            'helpButtonTitle': 'Erklärung des Diagramms'
                        },
                        'skillsProfile': {
                            'tabTitle': 'Kompetenzprofil',
                            'noTopicMessage': 'Es kann kein Kompetenzprofil angezeigt werden, da für dieses Lernpaket keine Themen definiert wurden.',
                            'learningGoalLabel': 'Lernziel',
                            'learningProgressLabel': 'Lernfortschritt in %',
                            'learningProgressTooltip': 'Lernfortschritt',
                            'numberOfQuestionsLabel': 'Anzahl Fragen',
                            'questionsWithoutTopic': 'Fragen ohne Thema',
                            'helpButtonTitle': 'Erklärung des Diagramms'
                        }
                    }
                },
                'ranking': {
                    'allTime': 'allzeit',
                    'points': 'Pkt.',
                    'noOrganizationRanking': 'Für diesen Zeitraum sind keine Daten vorhanden.',
                    'noRankingData': 'Keine Rangliste verfügbar',
                    'currentMonth': 'aktueller Monat',
                    'lastMonth': 'letzter Monat',
                    'currentYear': 'aktuelles Jahr',
                    'lastYear': 'letztes Jahr',
                    'noData': 'Die Rangliste ist für diese Organisation deaktiviert'
                },
                'profile': {
                    'email': 'E-Mail',
                    'emailAndIdentifier': 'Benutzer / E‑Mail',
                    'identifier': 'Benutzer',
                    'name': 'Name',
                    'organizationName': 'Organisation',
                    'close': 'Schliessen',
                    'title': 'Profil',
                    'noOrganization': 'Keine Organisation',
                    'usePseudonym': 'Pseudonym anzeigen',
                    'activePseudonym': 'Ja',
                    'inactivePseudonym': 'Nein',
                    'pseudonym': 'Pseudonym',
                    'cancel': 'Verwerfen',
                    'save': 'Speichern',
                    'editButton': 'bearbeiten',
                    'monthlyReport': 'Monatlicher Fortschrittsbericht',
                    'monthlyReportSaved': 'Einstellungen gespeichert',
                    'locale': 'Sprache:',
                    'mainChannelLabel': 'Hauptkanal',
                    'otherChannels': 'Weitere Kanäle',
                    'msTeamsChannel': 'Microsoft Teams',
                    'msTeamsMicromateChannel': 'Microsoft Teams (Micromate)',
                    'msTeamsTaskbaseChannel': 'Microsoft Teams (Taskbase)',
                    'slackChannel': 'Slack',
                    'googleChatChannel': 'Google Chat',
                    'learnChannel': 'Learn Micromate (Web)',
                    'moodleChannel': 'Moodle',
                    'learningReminder': 'Lernerinnerung',
                    'learningReminderOn': 'Aktiv',
                    'learningReminderOff': 'Inaktiv',
                    'learningReminderFrequency': 'Frequenz',
                    'learningReminderFrequency_weekly': 'Wöchentlich',
                    'learningReminderFrequency_biweekly': 'Zweiwöchentlich',
                    'learningReminderFrequency_monthly': 'Monatlich'
                },
                'moodle-widget-badge': {
                    'wrong-configuration': '{{appName}} ist nicht korrekt konfiguriert und kann darum nicht verwendet werden.',
                    'clickToStartSession': 'Neue Lernsession starten...',
                    'title': 'Mit {{appName}} <strong class="micromate-link-title-package-name">{{learningPackageName}}</strong> lernen',
                    'micromateWillContactSoon': '{{tutorName}} kontaktiert dich in Kürze...',
                    'couldNotStartMicromate': 'Lernpakete konnte nicht gestartet werden.',
                    'waitingForConnection': 'Warte auf Verbindung mit {{tutorName}}...'
                },
                'navigation': {
                    'chat': 'Chat',
                    'badges': 'Badges',
                    'studyProgress': 'Lernfortschritt',
                    'scoreboard': 'Rangliste',
                    'profile': 'Profil'
                },
                'general': {
                    'alreadyConnected': '{{appName}} ist bereits in einem anderen Fenster geöffnet.',
                    'autherror': 'Bei der Anmeldung bei {{appName}} ist etwas schiefgelaufen. Bitte versuche es erneut. Sollte das Problem weiterhin bestehen, dann melde dich unter {{supportMail}}.',
                    'autherrorButton': 'Anmeldung erneut versuchen',
                    'missingRole': 'Du verfügst nicht über die benötigten Berechtigungen. Melde dich bei deinem {{appName}} Verantwortlichen.',
                    'missingRoleButton': 'Abmelden',
                    'useMicromateHere': '{{appName}} hier verwenden',
                    'endOfHistoryMessage': 'Keine weiteren Nachrichten verfügbar.'
                },
                'shared': {
                    'locals': {
                        'af': 'Afrikaans',
                        'sq': 'Albanisch',
                        'eu': 'Baskisch',
                        'bs': 'Bosnisch',
                        'ca': 'Katalanisch',
                        'hr': 'Kroatisch',
                        'cs': 'Tschechisch',
                        'da': 'Dänisch',
                        'nl-nl': 'Niederländisch (Niederlande)',
                        'nl-be': 'Niederländisch (Belgien)',
                        'en-us': 'Englisch (USA)',
                        'en-gb': 'Englisch (Großbritannien)',
                        'en-ca': 'Englisch (Kanada)',
                        'en-au': 'Englisch (Australien)',
                        'en-in': 'Englisch (Indien)',
                        'en-sg': 'Englisch (Singapur)',
                        'en-nz': 'Englisch (Neuseeland)',
                        'en-za': 'Englisch (Südafrika)',
                        'en-ie': 'Englisch (Irland)',
                        'en-ph': 'Englisch (Philippinen)',
                        'et': 'Estnisch',
                        'fil': 'Filipino',
                        'fi': 'Finnisch',
                        'fr-fr': 'Französisch (Frankreich)',
                        'fr-ca': 'Französisch (Kanada)',
                        'fr-be': 'Französisch (Belgien)',
                        'fr-ch': 'Französisch (Schweiz)',
                        'fr-lu': 'Französisch (Luxemburg)',
                        'fr-mc': 'Französisch (Monaco)',
                        'gl': 'Galizisch',
                        'de-at': 'Deutsch (Österreich)',
                        'de-ch': 'Deutsch (Schweiz)',
                        'de-de': 'Deutsch (Deutschland)',
                        'de-lu': 'Deutsch (Luxemburg)',
                        'de-li': 'Deutsch (Liechtenstein)',
                        'hu': 'Ungarisch',
                        'is': 'Isländisch',
                        'id': 'Indonesisch',
                        'ga': 'Irisch',
                        'it': 'Italienisch',
                        'lv': 'Lettisch',
                        'lt': 'Litauisch',
                        'lb': 'Luxemburgisch',
                        'mt': 'Maltesisch',
                        'nb': 'Norwegisch Bokmål',
                        'pl': 'Polnisch',
                        'pt-br': 'Portugiesisch (Brasilien)',
                        'pt-pt': 'Portugiesisch (Portugal)',
                        'ro': 'Rumänisch',
                        'gd': 'Schottisch-Gälisch',
                        'sr': 'Serbisch',
                        'sk': 'Slowakisch',
                        'sl': 'Slowenisch',
                        'es-es': 'Spanisch (Spanien)',
                        'es-mx': 'Spanisch (Mexiko)',
                        'es-us': 'Spanisch (USA)',
                        'es-co': 'Spanisch (Kolumbien)',
                        'es-ar': 'Spanisch (Argentinien)',
                        'es-ve': 'Spanisch (Venezuela)',
                        'es-cl': 'Spanisch (Chile)',
                        'es-pe': 'Spanisch (Peru)',
                        'es-ec': 'Spanisch (Ecuador)',
                        'es-pr': 'Spanisch (Puerto Rico)',
                        'sv': 'Schwedisch',
                        'tr': 'Türkisch',
                        'vi': 'Vietnamesisch',
                        'cy': 'Walisisch'
                    }
                },
                'registration': {
                    'flows': {
                        'invitation': {
                            'title': '{{appName}} Registrierung abschliessen',
                            'alreadyInitializedTitle': 'Ungültige Einladung',
                            'alreadyInitialized': 'Diese Einladung ist nicht mehr gültig. Kontaktiere die verantwortliche Person in deiner Organisation.<br><br>Falls du bereits registriert bist, kannst du dich mit deiner E-Mail und deinem Passwort unter <a href="https://learn.micromate.ai">learn.micromate.ai</a> anmelden.'
                        },
                        'selfregistrationwithorganisation': {
                            'title': '{{appName}} Registrierung',
                            'invalidOrgCodeTitle': 'Ungültiger Registrierungslink',
                            'invalidOrgCode': 'Dieser Registrierungslink ist nicht gültig. Kontaktiere die verantwortliche Person in deiner Organisation.',
                            'joinByOrgCodeDisabledTitle': 'Ungültiger Registrierungslink',
                            'joinByOrgCodeDisabled': 'Dieser Registrierungslink ist nicht gültig. Kontaktiere die verantwortliche Person in deiner Organisation.',
                            'emailAlreadyTakenTitle': 'E-Mail bereits vergeben',
                            'emailAlreadyTaken': 'Die angegebene E-Mail-Adresse ist bereits vergeben.<br><br>Falls du bereits registriert bist, kannst du dich mit deiner E-Mail und deinem Passwort unter <a href="https://learn.micromate.ai">learn.micromate.ai</a> anmelden.',
                            'domainValidationFailedTitle': 'E-Mail ungültig',
                            'domainValidationFailed': 'Zur Registrierung ist nur eine E-Mail der Organisation {{organizationName}} zulässig.'
                        },
                        'selfregistrationwithoutorganisation': {
                            'title': '{{appName}} Registrierung',
                            'emailAlreadyTakenTitle': 'E-Mail bereits vergeben',
                            'emailAlreadyTaken': 'Die angegebene E-Mail-Adresse ist bereits vergeben.<br><br>Falls du bereits registriert bist, kannst du dich mit deiner E-Mail und deinem Passwort unter <a href="https://learn.micromate.ai">learn.micromate.ai</a> anmelden.'
                        }
                    },
                    'organization': 'Organisation',
                    'email': 'E-Mail',
                    'firstName': 'Vorname',
                    'emailRequiredError': 'E-Mail muss angegeben werden',
                    'emailInvalidError': 'E-Mail ungültig',
                    'firstNameRequiredError': 'Vorname muss angegeben werden',
                    'lastName': 'Nachname',
                    'lastNameRequiredError': 'Nachname muss angegeben werden',
                    'language': 'Sprache',
                    'password': 'Passwort',
                    'passwordInvalidError': 'Ungültiges Passwort Format',
                    'passwordComplexityPolicyMinLength': 'Muss mindestens die Länge {{minLength}} haben',
                    'passwordComplexityPolicyHasNumber': 'Muss eine Zahl enthalten',
                    'passwordComplexityPolicyHasLowercase': 'Muss einen Kleinbuchstaben enthalten',
                    'passwordComplexityPolicyHasSymbol': 'Muss ein Symbol enthalten (z.B. $%*!?)',
                    'passwordComplexityPolicyHasUppercase': 'Muss einen Grossbuchstaben enthalten',
                    'confirmPassword': 'Passwort (Wiederholung)',
                    'confirmPasswordInvalidError': 'Passwort stimmt nicht überein',
                    'agbLinkTitle': 'Allgemeine Geschäftsbedingungen',
                    'dataPrivacyLinkTitle': 'Datenschutzbestimmungen',
                    'dataAccept': 'akzeptieren',
                    'registrationButton': 'Registrieren',
                    'registrationFailedErrorTitle': 'Registrierung fehlgeschlagen',
                    'registrationFailedError': 'Registration konnte nicht abgeschlossen werden. Bitte melde dich bei',
                    'invalidCodeError': 'Diese Einladung ist nicht mehr gültig. Falls du eine neue Einladung benötigst, melde dich bei deinem {{appName}}-Verantwortlichen.',
                    'exchangeCodeError': 'Registration aktuell nicht möglich. Bitte melde dich bei',
                    'parameterErrorTitle': 'Fehlerhafte Parameter',
                    'parameterError': 'Die angegebenen Parameter sind ungültig. Kontaktiere die verantwortliche Person in deiner Organisation.',
                    'confirmation': {
                        'title': 'Verifizierungsmail versendet',
                        'description': 'Wir haben dir eine E-Mail zur Bestätigung deiner E-Mailadresse an <strong>{{email}}</strong> gesendet. Klick auf den Link im E-Mail, um die Registrierung abzuschliessen. <br><br>Prüfe deinen Spamordner, falls du keine E-Mail bekommen hast.'
                    }
                },
                'feedback': {
                    'buttonTitle': 'Feedback geben',
                    'title': 'Verfasse dein Feedback',
                    'sendButton': 'Senden',
                    'infoBox': 'Zur Korrespondenz wird dein Name und deine E-Mail übermittelt',
                    'resultTitle': 'Danke für dein Feedback',
                    'resultSuccessMessage': 'Dein Feedback wurde der verantwortlichen Person übermittelt.',
                    'resultErrorMessage': 'Das Feedback konnte nicht eingereicht werden.',
                    'resultNotAllowFeedbackErrorMessage': 'Die Feedbackfunktion steht dir für diese Frage bzw. dieses Lernpaket nicht mehr zur Verfügung.',
                    'resultCloseButton': 'Schliessen',
                    'mainTopicTitle': 'Zu was willst du Feedback geben?',
                    'topicQuestionButton': 'Aktuelle Frage',
                    'topicLearningPackageButton': 'Aktuelles Lernpaket',
                    'topicTitle': 'Was willst du melden?',
                    'questionFaultyTopic': 'Die Aufgabe ist fehlerhaft',
                    'questionUnintelligibleTopic': 'Die Aufgabe ist unverständlich',
                    'questionSpellingTopic': 'Diese Aufgabe enthält Rechtschreibfehler',
                    'questionIdeaTopic': 'Ich habe eine Idee oder Anmerkung',
                    'learningPackageQuestionSuggestionTopic': 'Konkrete Frage vorschlagen',
                    'learningPackageTopicSuggestionTopic': 'Neues Inhaltsthema vorschlagen',
                    'learningPackageFeedbackTopic': 'Feedback zum Lernpaket geben',
                    'questionPreviewHide': 'Frage ausblenden',
                    'questionPreviewShow': 'Frage einblenden'
                },
                'popup': {
                    'wizardBackButton': 'Zurück'
                },
                'questionPreview': {
                    'questionTitle': 'Frage',
                    'answerTitle': 'Antworten',
                    'answerFalse': 'Falsch',
                    'answerTrue': 'Richtig',
                    'explanationTitle': 'Erläuterung',
                    'sourceTitle': 'Quelle'
                },
                'slack': {
                    'success': {
                        'title': 'Installation erfolgreich',
                        'description': 'Die Taskbase App wurde erfolgreich installiert und kann nun verwendet werden.',
                        'button': 'Taskbase App öffnen'
                    },
                    'failure': {
                        'title': 'Installation fehlgeschlagen',
                        'description': 'Leider konnte die Installation der Taskbase App nicht abgeschlossen werden. Überprüfe den Slack Workspace Identifikator in Manage Micromate und installiere die Taskbase App erneut. Weitere Informationen findest du in unserer Hilfe.',
                        'button': 'Hilfe aufrufen'
                    }
                }
            }
        },
        {
            language: 'en',
            translations: {
                'profileLearnerMissing': {
                    'title': 'Profile not ready yet',
                    'description': 'Your profile could not be found yet. Start a chat in this app first, then try again.',
                    'retry': 'Try again'
                },
                'resourceViewer': {
                    'notAvailableError': {
                        'title': 'Resource not available',
                        'description': 'Sorry, the requested resource is not available.'
                    }
                },
                'badge': {
                    'locked': 'Locked',
                    'level': 'Level',
                    'noLevel': 'Reached',
                    'noData': 'Badge overview is disabled for this organization'
                },
                'learningGoal': {
                    'newbie': 'Newbie',
                    'beginners': 'Beginners',
                    'advanced': 'Advanced',
                    'skilled': 'Skilled',
                    'professional': 'Professional',
                    'expert': 'Expert',
                    'noData': 'No unlocked learning packages',
                    'noIntroduction': 'No description available',
                    'feedback': 'Give feedback',
                    'releaseDate': 'Last update:',
                    'ownerOrganization': 'Created by',
                    'checkSticker': 'Learning goal achieved',
                    'forgettingApplied': 'Time to learn',
                    'navLearningProgress': 'Learning goal and progress',
                    'navQuizzesOverview': 'Visualization of learning progress',
                    'navLearningPackageInfo': {
                        'learningPackage': 'Learning Package Insights',
                        'managedTutor': 'Tutor Insights'
                    },
                    'learningGoal': 'Learning goal',
                    'numberOfQuestion': 'Progress of {{numberOfQuestion}} questions',
                    'unansweredQuestions': 'of which {{neverAnsweredQuestions}} unanswered',
                    'unansweredAndInactiveQuestions': 'of which {{neverAnsweredQuestions}} unanswered and {{numberOfInactiveQuestions}} inactive',
                    'inactiveQuestions': 'of which {{numberOfInactiveQuestions}} inactive',
                    'learnButton': {
                        'learningPackage': 'Learn this learning package',
                        'managedTutor': 'Start this tutor'
                    },
                    'inactive': 'inactive',
                    'learningHistoryCopyToast': 'Learning history copied',
                    'insightsPopup': {
                        'learningProgress': 'Learning progress',
                        'forgetting': 'Forgetting',
                        'details': {
                            'tabTitle': 'Details',
                            'learningTitle': 'Start Learning',
                            'feedback': 'Give Feedback',
                            'learning': {
                                'learningPackage': 'Learn package',
                                'managedTutor': 'Start tutor'
                            },
                            'buttonTitle': 'Give feedback',
                            'availableLanguages': 'Available languages',
                            'noIntroduction': 'No description available',
                            'releaseDate': 'Last update'
                        },
                        'feedback': {
                            'tabTitle': 'Feedback',
                            'clearCurrentFeedbackButton': 'Discard feedback',
                            'startNewFeedbackButton': 'Start new Feedback'
                        },
                        'questions': {
                            'tabTitle': 'Question Map',
                            'deactivatedQuestionLegend': 'Inactive question',
                            'notAnsweredQuestionLegend': 'Unanswered question',
                            'numberOfQuestions': 'This learning package <strong>contains {{numberOfQuestion}} question</strong>',
                            'allQuestionsAnswered': ', which you have already <strong>answered at least once</strong>. ',
                            'noAnsweredQuestions': '. You haven’t <strong>answered any questions yet</strong>. ',
                            'notShowedYetQuestions': '. There are <strong>{{neverAnsweredQuestions}} questions you haven’t seen yet</strong>. ',
                            'inactiveQuestions': 'Additionally, <strong>{{numberOfInactiveQuestions}} questions are inactive</strong>. ',
                            'allInactiveQuestions': '<strong>All</strong> of them are <strong>inactive</strong>. ',
                            'colorExplanation': 'Green questions you answer correctly consistently. Red questions indicate incorrect answers.',
                            'helpButtonTitle': 'Explanation of the chart'
                        },
                        'topics': {
                            'tabTitle': 'Topics',
                            'noData': 'Start a conversation with the tutor to see your progress!',
                            'colorExplanation': 'The color of each square shows your mastery of that topic.',
                            'masteryLegend': 'Mastery'
                        },
                        'learningProgressHistory': {
                            'tabTitle': 'Learning Progress',
                            'correctAnswersLabel': 'Correct answers',
                            'incorrectAnswersLabel': 'Incorrect answers',
                            'answeredQuestions': 'Answered Questions',
                            'dayLabel': 'Days',
                            'weekLabel': 'Weeks',
                            'monthLabel': 'Months',
                            'learningProgressLabel': 'Learning progress',
                            'learningProgressTooltip': 'Learning progress in %',
                            'weekShortVersion': 'Wk',
                            'learningGoalLabel': 'Learning goal',
                            'forgettingLabel': 'Forgetting',
                            'timeOfForgettingLabel': 'Time of forgetting',
                            'nowLabel': 'Now',
                            'dailyMode': 'Day',
                            'weeklyMode': 'Week',
                            'monthlyMode': 'Month',
                            'helpButtonTitle': 'Explanation of the chart'
                        },
                        'skillsProfile': {
                            'tabTitle': 'Skill Profile',
                            'noTopicMessage': 'No skill profile can be displayed because no topics have been defined for this learning package.',
                            'learningProgressLabel': 'Learning progress in %',
                            'learningProgressTooltip': 'Learning progress',
                            'learningGoalLabel': 'Learning goal',
                            'numberOfQuestionsLabel': 'Number of Questions',
                            'questionsWithoutTopic': 'Questions without Topic',
                            'helpButtonTitle': 'Explanation of the chart'
                        }
                    }
                },
                'ranking': {
                    'allTime': 'all-time',
                    'points': 'pts',
                    'noOrganizationRanking': 'No data available for this period.',
                    'noRankingData': 'No ranking available',
                    'currentMonth': 'current month',
                    'lastMonth': 'last month',
                    'currentYear': 'current year',
                    'lastYear': 'last year',
                    'noData': 'Ranking is disabled for this organization'
                },
                'profile': {
                    'email': 'E-Mail',
                    'emailAndIdentifier': 'User / E‑Mail',
                    'identifier': 'User',
                    'name': 'Name',
                    'organizationName': 'Organisation',
                    'close': 'Close',
                    'title': 'Profile',
                    'noOrganization': 'No organization',
                    'usePseudonym': 'Show pseudonym',
                    'activePseudonym': 'Yes',
                    'inactivePseudonym': 'No',
                    'pseudonym': 'Pseudonym',
                    'cancel': 'Cancel',
                    'save': 'Save',
                    'editButton': 'edit',
                    'monthlyReport': 'Monthly progress report',
                    'monthlyReportSaved': 'Settings saved',
                    'locale': 'Language:',
                    'mainChannelLabel': 'Main Channel',
                    'otherChannels': 'Other channels',
                    'msTeamsChannel': 'Microsoft Teams',
                    'msTeamsMicromateChannel': 'Microsoft Teams (Micromate)',
                    'msTeamsTaskbaseChannel': 'Microsoft Teams (Taskbase)',
                    'slackChannel': 'Slack',
                    'googleChatChannel': 'Google Chat',
                    'learnChannel': 'Learn Micromate (Web)',
                    'moodleChannel': 'Moodle',
                    'learningReminder': 'Learning Reminder',
                    'learningReminderOn': 'On',
                    'learningReminderOff': 'Off',
                    'learningReminderFrequency': 'Frequency',
                    'learningReminderFrequency_weekly': 'Weekly',
                    'learningReminderFrequency_biweekly': 'Biweekly',
                    'learningReminderFrequency_monthly': 'Monthly'
                },
                'moodle-widget-badge': {
                    'wrong-configuration': '{{appName}} is not configured correctly and therefore cannot be used.',
                    'clickToStartSession': 'Start a new learning session...',
                    'title': 'Learn <strong class="micromate-link-title-package-name">{{learningPackageName}}</strong> with {{appName}}',
                    'micromateWillContactSoon': '{{tutorName}} will contact you shortly...',
                    'couldNotStartMicromate': 'Learning packages could not be started.',
                    'waitingForConnection': 'Waiting for connection with {{tutorName}}...'
                },
                'navigation': {
                    'chat': 'Chat',
                    'badges': 'Badges',
                    'studyProgress': 'Study Progress',
                    'scoreboard': 'Scoreboard',
                    'profile': 'Profile'
                },
                'general': {
                    'alreadyConnected': '{{appName}} is already open in another window. Close it and reload the page.',
                    'autherror': 'Something went wrong when registering with {{appName}}. Please try again. If the problem persists, please contact us at {{supportMail}}.',
                    'autherrorButton': 'Try logging in again',
                    'missingRole': 'You do not have the required authorizations. Please contact your {{appName}} representative.',
                    'missingRoleButton': 'Log out',
                    'useMicromateHere': 'Use {{appName}} here',
                    'endOfHistoryMessage': 'No further messages available.'
                },
                'shared': {
                    'locals': {
                        'af': 'Afrikaans',
                        'sq': 'Albanian',
                        'eu': 'Basque',
                        'bs': 'Bosnian',
                        'ca': 'Catalan',
                        'hr': 'Croatian',
                        'cs': 'Czech',
                        'da': 'Danish',
                        'nl-nl': 'Dutch (Netherlands)',
                        'nl-be': 'Dutch (Belgium)',
                        'en-us': 'English (USA)',
                        'en-gb': 'English (UK)',
                        'en-ca': 'English (Canada)',
                        'en-au': 'English (Australia)',
                        'en-in': 'English (India)',
                        'en-sg': 'English (Singapore)',
                        'en-nz': 'English (New Zealand)',
                        'en-za': 'English (South Africa)',
                        'en-ie': 'English (Ireland)',
                        'en-ph': 'English (Philippines)',
                        'et': 'Estonian',
                        'fil': 'Filipino',
                        'fi': 'Finnish',
                        'fr-fr': 'French (France)',
                        'fr-ca': 'French (Canada)',
                        'fr-be': 'French (Belgium)',
                        'fr-ch': 'French (Switzerland)',
                        'fr-lu': 'French (Luxembourg)',
                        'fr-mc': 'French (Monaco)',
                        'gl': 'Galician',
                        'de-at': 'German (Austria)',
                        'de-ch': 'German (Switzerland)',
                        'de-de': 'German (Germany)',
                        'de-lu': 'German (Luxembourg)',
                        'de-li': 'German (Liechtenstein)',
                        'hu': 'Hungarian',
                        'is': 'Icelandic',
                        'id': 'Indonesian',
                        'ga': 'Irish',
                        'it': 'Italian',
                        'lv': 'Latvian',
                        'lt': 'Lithuanian',
                        'lb': 'Luxembourgish',
                        'mt': 'Maltese',
                        'nb': 'Norwegian Bokmål',
                        'pl': 'Polish',
                        'pt-br': 'Portuguese (Brazil)',
                        'pt-pt': 'Portuguese (Portugal)',
                        'ro': 'Romanian',
                        'gd': 'Scottish Gaelic',
                        'sr': 'Serbian',
                        'sk': 'Slovak',
                        'sl': 'Slovenian',
                        'es-es': 'Spanish (Spain)',
                        'es-mx': 'Spanish (Mexico)',
                        'es-us': 'Spanish (USA)',
                        'es-co': 'Spanish (Colombia)',
                        'es-ar': 'Spanish (Argentina)',
                        'es-ve': 'Spanish (Venezuela)',
                        'es-cl': 'Spanish (Chile)',
                        'es-pe': 'Spanish (Peru)',
                        'es-ec': 'Spanish (Ecuador)',
                        'es-pr': 'Spanish (Puerto Rico)',
                        'sv': 'Swedish',
                        'tr': 'Turkish',
                        'vi': 'Vietnamese',
                        'cy': 'Welsh'
                    }
                },
                'registration': {
                    'flows': {
                        'invitation': {
                            'title': 'Complete {{appName}} registration',
                            'alreadyInitializedTitle': 'Invalid invitation',
                            'alreadyInitialized': 'This invitation is no longer valid. Contact the person responsible in your organization.<br><br>If you are already registered, you can log in with your e-mail and password at <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        },
                        'selfregistrationwithorganisation': {
                            'title': '{{appName}} registration',
                            'invalidOrgCodeTitle': 'Invalid registration link',
                            'invalidOrgCode': 'This registration link is not valid. Contact the responsible person in your organization.',
                            'joinByOrgCodeDisabledTitle': 'Invalid registration link',
                            'joinByOrgCodeDisabled': 'This registration link is not valid. Contact the responsible person in your organization.',
                            'emailAlreadyTakenTitle': 'E-mail already taken',
                            'emailAlreadyTaken': 'The e-mail address you entered is already taken.<br><br>If you are already registered, you can log in with your e-mail and password at <a href="https://learn.micromate.ai">learn.micromate.ai</a>.',
                            'domainValidationFailedTitle': 'E-mail invalid',
                            'domainValidationFailed': 'Only an e-mail from the organization {{organizationName}} is permitted for registration.'
                        },
                        'selfregistrationwithoutorganisation': {
                            'title': '{{appName}} registration',
                            'emailAlreadyTakenTitle': 'E-mail already taken',
                            'emailAlreadyTaken': 'The e-mail address you entered is already taken.<br><br>If you are already registered, you can log in with your e-mail and password at <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        }
                    },
                    'organization': 'Organization',
                    'email': 'E-mail',
                    'emailRequiredError': 'E-mail must be specified',
                    'emailInvalidError': 'E-mail not valid',
                    'firstName': 'First name',
                    'firstNameRequiredError': 'First name must be specified',
                    'lastName': 'Last name',
                    'lastNameRequiredError': 'Last name must be specified',
                    'language': 'Language',
                    'password': 'Password',
                    'passwordInvalidError': 'Invalid password format',
                    'passwordComplexityPolicyMinLength': 'Must have a minimum length of {{minLength}}',
                    'passwordComplexityPolicyHasNumber': 'Must contain a number',
                    'passwordComplexityPolicyHasLowercase': 'Must contain a lowercase letter',
                    'passwordComplexityPolicyHasSymbol': 'Must contain a symbol (e.g. $%*!?)',
                    'passwordComplexityPolicyHasUppercase': 'Must contain an uppercase letter',
                    'confirmPassword': 'Password (repeat)',
                    'confirmPasswordInvalidError': 'Password does not match',
                    'agbLinkTitle': 'General terms and conditions',
                    'dataPrivacyLinkTitle': 'Privacy policy',
                    'dataAccept': 'accepted',
                    'registrationButton': 'Register',
                    'registrationFailedErrorTitle': 'Registration failed',
                    'registrationFailedError': 'Registration could not be completed. Please contact',
                    'invalidCodeError': 'This invitation is no longer valid. If you need a new invitation, please contact your {{appName}} representative.',
                    'exchangeCodeError': 'Registration currently not possible. Please contact',
                    'parameterErrorTitle': 'Incorrect parameters',
                    'parameterError': 'The specified parameters are invalid. Contact the person responsible in your organization.',
                    'confirmation': {
                        'title': 'Verification mail sent',
                        'description': 'We have sent you an email to <strong>{{email}}</strong> to confirm your email address. Click on the link in the email to finalise your registration. Check your spam folder if you have not received an email.'
                    }
                },
                'feedback': {
                    'buttonTitle': 'Give feedback',
                    'title': 'Write your feedback',
                    'sendButton': 'Send',
                    'infoBox': 'Your name and e-mail will be transmitted for correspondence',
                    'resultTitle': 'Thank you for your feedback',
                    'resultSuccessMessage': 'Your feedback has been sent to the person responsible.',
                    'resultErrorMessage': 'The feedback could not be submitted.',
                    'resultNotAllowFeedbackErrorMessage': 'The feedback function is no longer available for this question or learning package.',
                    'resultCloseButton': 'Close',
                    'mainTopicTitle': 'What would you like to give feedback on?',
                    'topicQuestionButton': 'Current question',
                    'topicLearningPackageButton': 'Current learning package',
                    'topicTitle': 'What do you want to report?',
                    'questionFaultyTopic': 'The task is incorrect',
                    'questionUnintelligibleTopic': 'The task is incomprehensible',
                    'questionSpellingTopic': 'This task contains spelling mistakes',
                    'questionIdeaTopic': 'I have an idea or comment',
                    'learningPackageQuestionSuggestionTopic': 'Suggest a specific question',
                    'learningPackageTopicSuggestionTopic': 'Suggest a new content topic',
                    'learningPackageFeedbackTopic': 'Giving feedback on the learning package',
                    'questionPreviewHide': 'Hide question',
                    'questionPreviewShow': 'Show question'
                },
                'popup': {
                    'wizardBackButton': 'Back'
                },
                'questionPreview': {
                    'questionTitle': 'Question',
                    'answerTitle': 'Answer',
                    'answerFalse': 'False',
                    'answerTrue': 'True',
                    'explanationTitle': 'Explanation',
                    'sourceTitle': 'Source'
                },
                'slack': {
                    'success': {
                        'title': 'Installation successful',
                        'description': 'The Taskbase app has been successfully installed and can now be used.',
                        'button': 'Open Taskbase App'
                    },
                    'failure': {
                        'title': 'Installation failed',
                        'description': 'Unfortunately, the installation of the Taskbase app could not be completed. Please check the Slack Workspace identifier in Manage Micromate and reinstall the Taskbase app. For more information, please refer to our help documentation.',
                        'button': 'Get help'
                    }
                }
            }
        },
        {
            language: 'fr',
            translations: {
                'profileLearnerMissing': {
                    'title': 'Profil pas encore prêt',
                    'description': 'Ton profil est introuvable pour le moment. Commence d\'abord une discussion dans cette application, puis réessaie.',
                    'retry': 'Réessayer'
                },
                'resourceViewer': {
                    'notAvailableError': {
                        'title': 'Ressource non disponible',
                        'description': 'Désolé, la ressource demandée n\'est pas disponible.'
                    }
                },
                'badge': {
                    'locked': 'Verroutllé',
                    'level': 'Niveau',
                    'noLevel': 'Atteint',
                    'noData': 'L’aperçu des badges est désactivé pour cette organisation'
                },
                'learningGoal': {
                    'newbie': 'nouveau',
                    'beginners': 'débutant',
                    'advanced': 'avancé',
                    'skilled': 'expérimenté',
                    'professional': 'professionnel',
                    'expert': 'expert',
                    'noData': 'Aucun package d\'apprentissage déverrouillé',
                    'noIntroduction': 'Pas de description disponible',
                    'feedback': 'Donner un feedback',
                    'releaseDate': 'Dernière mise à jour:',
                    'ownerOrganization': 'Créé par',
                    'checkSticker': 'Objectif d\'apprentissage atteint',
                    'forgettingApplied': 'Temps d\'apprendre',
                    'navLearningProgress': 'Objectif d\'apprentissage et progression',
                    'navQuizzesOverview': 'Visualisation de la progression de formation',
                    'navLearningPackageInfo': {
                        'learningPackage': 'Pack d’apprentissage Insights',
                        'managedTutor': 'Tuteur Insights'
                    },
                    'learningGoal': 'Objectif d\'apprentissage',
                    'numberOfQuestion': 'Progression de {{numberOfQuestion}} questions',
                    'unansweredQuestions': 'dont {{neverAnsweredQuestions}} sans réponse',
                    'unansweredAndInactiveQuestions': 'dont {{neverAnsweredQuestions}} sans réponse et {{numberOfInactiveQuestions}} inactives',
                    'inactiveQuestions': 'dont {{numberOfInactiveQuestions}} inactives',
                    'learnButton': {
                        'learningPackage': 'Apprenez ce module d\'apprentissage',
                        'managedTutor': 'Lancer ce tuteur'
                    },
                    'inactive': 'inactif',
                    'learningHistoryCopyToast': 'Historique d’apprentissage copié',
                    'insightsPopup': {
                        'learningProgress': 'Progression de formation',
                        'forgetting': 'Oubli',
                        'details': {
                            'tabTitle': 'Détails',
                            'learningTitle': 'Commencer l’apprentissage',
                            'feedback': 'Donner un feedback',
                            'learning': {
                                'learningPackage': 'Apprendre le pack',
                                'managedTutor': 'Début du tutorat'
                            },
                            'buttonTitle': 'Donner un feedback',
                            'availableLanguages': 'Langues disponibles',
                            'noIntroduction': 'Pas de description disponible',
                            'releaseDate': 'Dernière mise à jour'
                        },
                        'feedback': {
                            'tabTitle': 'Feedback',
                            'clearCurrentFeedbackButton': 'Rejeter le feedback',
                            'startNewFeedbackButton': 'Commencer un nouveau feedback'
                        },
                        'questions': {
                            'tabTitle': 'Plan questions',
                            'deactivatedQuestionLegend': 'Question inactive',
                            'notAnsweredQuestionLegend': 'Question sans réponse',
                            'numberOfQuestions': 'Ce pack d\'apprentissage <strong>{{numberOfQuestion}} question</strong>',
                            'allQuestionsAnswered': ', auxquelles vous avez déjà répondu <strong>au moins une fois</strong>. ',
                            'noAnsweredQuestions': '. Vous n’avez encore répondu à <strong>aucune question</strong>. ',
                            'notShowedYetQuestions': '. Il y a <strong>{{neverAnsweredQuestions}} questions qui ne vous ont pas encore été montrées</strong>. ',
                            'inactiveQuestions': 'De plus, <strong>{{numberOfInactiveQuestions}} questions sont inactives</strong>. ',
                            'allInactiveQuestions': 'Ils sont <strong>tous inactives</strong>. ',
                            'colorExplanation': 'Les questions vertes sont répondues correctement de manière durable. Les questions rouges indiquent des réponses incorrectes.',
                            'helpButtonTitle': 'Explication du diagramme'
                        },
                        'topics': {
                            'tabTitle': 'Sujets',
                            'noData': 'Commence une conversation avec le tuteur pour voir ta progression !',
                            'colorExplanation': 'La couleur de chaque carré montre votre maîtrise de ce sujet.',
                            'masteryLegend': 'Maîtrise'
                        },
                        'learningProgressHistory': {
                            'tabTitle': 'Progression de formation',
                            'correctAnswersLabel': 'Réponses correctes',
                            'incorrectAnswersLabel': 'Réponses incorrectes',
                            'answeredQuestions': 'Questions répondues',
                            'dayLabel': 'Jours',
                            'weekLabel': 'Semaines',
                            'monthLabel': 'Mois',
                            'learningProgressLabel': 'Progression d’apprentissage',
                            'learningProgressTooltip': 'Progression d’apprentissage en %',
                            'weekShortVersion': 'Sem',
                            'learningGoalLabel': 'Objectif d’apprentissage',
                            'forgettingLabel': 'Oubli',
                            'timeOfForgettingLabel': 'Moment de l’oubli',
                            'nowLabel': 'Maintenant',
                            'dailyMode': 'Jour',
                            'weeklyMode': 'Semaine',
                            'monthlyMode': 'Mois',
                            'helpButtonTitle': 'Explication du diagramme'
                        },
                        'skillsProfile': {
                            'tabTitle': 'Compétences',
                            'noTopicMessage': 'Aucun profil de compétences ne peut être affiché, car aucun sujet n’a été défini pour ce module d’apprentissage.',
                            'learningProgressLabel': 'Progression d’apprentissage en %',
                            'learningProgressTooltip': 'Progression d’apprentissage',
                            'learningGoalLabel': 'Objectif d’apprentissage',
                            'numberOfQuestionsLabel': 'Nombre de questions',
                            'questionsWithoutTopic': 'Questions sans sujet',
                            'helpButtonTitle': 'Explication du diagramme'
                        }
                    }
                },
                'ranking': {
                    'allTime': 'toujours',
                    'points': 'points',
                    'noOrganizationRanking': 'Aucune donnée n\'est disponible pour cette période.',
                    'noRankingData': 'Aucun classement disponible',
                    'currentMonth': 'mois actuel',
                    'lastMonth': 'mois dernier',
                    'currentYear': 'année actuelle',
                    'lastYear': 'année dernière',
                    'noData': 'Le classement est désactivé pour cette organisation'
                },
                'profile': {
                    'email': 'E-mail',
                    'emailAndIdentifier': 'Utilisateur / E‑mail',
                    'identifier': 'Utilisateur',
                    'name': 'Nom',
                    'organizationName': 'Organisation',
                    'close': 'Fermer',
                    'title': 'Profil',
                    'noOrganization': 'Aucune organisation',
                    'usePseudonym': 'Afficher le pseudonyme',
                    'activePseudonym': 'Oui',
                    'inactivePseudonym': 'Non',
                    'pseudonym': 'Pseudonyme',
                    'cancel': 'Rejeter',
                    'save': 'Enregistrer',
                    'editButton': 'éditer',
                    'monthlyReport': 'Rapport d\'avancement mensuel',
                    'monthlyReportSaved': 'Paramètres enregistrés',
                    'locale': 'Langue:',
                    'mainChannelLabel': 'Canal principal',
                    'otherChannels': 'Autres canaux',
                    'msTeamsChannel': 'Microsoft Teams',
                    'msTeamsMicromateChannel': 'Microsoft Teams (Micromate)',
                    'msTeamsTaskbaseChannel': 'Microsoft Teams (Taskbase)',
                    'slackChannel': 'Slack',
                    'googleChatChannel': 'Google Chat',
                    'learnChannel': 'Learn Micromate (Web)',
                    'moodleChannel': 'Moodle',
                    'learningReminder': 'Rappel d\'apprentissage',
                    'learningReminderOn': 'Activé',
                    'learningReminderOff': 'Désactivé',
                    'learningReminderFrequency': 'Fréquence',
                    'learningReminderFrequency_weekly': 'Hebdomadaire',
                    'learningReminderFrequency_biweekly': 'Bihebdomadaire',
                    'learningReminderFrequency_monthly': 'Mensuel'
                },
                'moodle-widget-badge': {
                    'wrong-configuration': '{{appName}} n\'est pas configuré correctement et ne peut donc pas être utilisé.',
                    'clickToStartSession': 'Démarrer une nouvelle session d\'apprentissage...',
                    'title': 'Apprendre  <strong class="micromate-link-title-package-name">{{learningPackageName}}</strong> avec {{appName}}',
                    'micromateWillContactSoon': '{{tutorName}} te contactera sous peu...',
                    'couldNotStartMicromate': 'Les paquets d\'apprentissage n\'ont pas pu être démarrés.',
                    'waitingForConnection': 'En attente de connexion avec {{tutorName}}'
                },
                'navigation': {
                    'chat': 'Chat',
                    'badges': 'Badges',
                    'studyProgress': 'Progression de formation',
                    'scoreboard': 'Classement',
                    'profile': 'Profil'
                },
                'general': {
                    'alreadyConnected': '{{appName}} est déjà ouvert dans une autre fenêtre. Ferme-la et recharge la page.',
                    'autherror': 'Quelque chose s\'est mal passé lors de l\'inscription à {{appName}}. Merci de réessayer. Si le problème persiste, contacte-nous à l\'adresse {{supportMail}}.',
                    'autherrorButton': 'Réessayer de se connecter',
                    'missingRole': 'Tu ne disposes pas des autorisations nécessaires. Contacte ton responsable {{appName}}.',
                    'missingRoleButton': 'Se déconnecter',
                    'useMicromateHere': 'Utiliser {{appName}} ici',
                    'endOfHistoryMessage': 'Aucun autre message disponible.'
                },
                'shared': {
                    'locals': {
                        'af': 'Afrikaans',
                        'sq': 'Albanais',
                        'eu': 'Basque',
                        'bs': 'Bosnien',
                        'ca': 'Catalan',
                        'hr': 'Croate',
                        'cs': 'Tchèque',
                        'da': 'Danois',
                        'nl-nl': 'Néerlandais (Pays-Bas)',
                        'nl-be': 'Néerlandais (Belgique)',
                        'en-us': 'Anglais (États-Unis)',
                        'en-gb': 'Anglais (Royaume-Uni)',
                        'en-ca': 'Anglais (Canada)',
                        'en-au': 'Anglais (Australie)',
                        'en-in': 'Anglais (Inde)',
                        'en-sg': 'Anglais (Singapour)',
                        'en-nz': 'Anglais (Nouvelle-Zélande)',
                        'en-za': 'Anglais (Afrique du Sud)',
                        'en-ie': 'Anglais (Irlande)',
                        'en-ph': 'Anglais (Philippines)',
                        'et': 'Estonien',
                        'fil': 'Filipino',
                        'fi': 'Finnois',
                        'fr-fr': 'Français (France)',
                        'fr-ca': 'Français (Canada)',
                        'fr-be': 'Français (Belgique)',
                        'fr-ch': 'Français (Suisse)',
                        'fr-lu': 'Français (Luxembourg)',
                        'fr-mc': 'Français (Monaco)',
                        'gl': 'Galicien',
                        'de-at': 'Allemand (Autriche)',
                        'de-ch': 'Allemand (Suisse)',
                        'de-de': 'Allemand (Allemagne)',
                        'de-lu': 'Allemand (Luxembourg)',
                        'de-li': 'Allemand (Liechtenstein)',
                        'hu': 'Hongrois',
                        'is': 'Islandais',
                        'id': 'Indonésien',
                        'ga': 'Irlandais',
                        'it': 'Italien',
                        'lv': 'Letton',
                        'lt': 'Lituanien',
                        'lb': 'Luxembourgeois',
                        'mt': 'Maltais',
                        'nb': 'Norvégien Bokmål',
                        'pl': 'Polonais',
                        'pt-br': 'Portugais (Brésil)',
                        'pt-pt': 'Portugais (Portugal)',
                        'ro': 'Roumain',
                        'gd': 'Gaélique écossais',
                        'sr': 'Serbe',
                        'sk': 'Slovaque',
                        'sl': 'Slovène',
                        'es-es': 'Espagnol (Espagne)',
                        'es-mx': 'Espagnol (Mexique)',
                        'es-us': 'Espagnol (États-Unis)',
                        'es-co': 'Espagnol (Colombie)',
                        'es-ar': 'Espagnol (Argentine)',
                        'es-ve': 'Espagnol (Venezuela)',
                        'es-cl': 'Espagnol (Chili)',
                        'es-pe': 'Espagnol (Pérou)',
                        'es-ec': 'Espagnol (Équateur)',
                        'es-pr': 'Espagnol (Porto Rico)',
                        'sv': 'Suédois',
                        'tr': 'Turc',
                        'vi': 'Vietnamien',
                        'cy': 'Gallois'
                    }
                },
                'registration': {
                    'flows': {
                        'invitation': {
                            'title': 'Compléter l\'inscription {{appName}}',
                            'alreadyInitializedTitle': 'Invitation non valide',
                            'alreadyInitialized': 'Cette invitation n\'est plus valable. Contacte la personne responsable dans ton organisation.<br><br>Si tu es déjà enregistré, tu peux te connecter avec ton e-mail et ton mot de passe sur <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        },
                        'selfregistrationwithorganisation': {
                            'title': 'Enregistrement {{appName}}',
                            'invalidOrgCodeTitle': 'Lien d\'enregistrement non valide',
                            'invalidOrgCode': 'Ce lien d\'enregistrement n\'est pas valide. Contacte la personne responsable dans ton organisation.',
                            'joinByOrgCodeDisabledTitle': 'Lien d\'enregistrement non valide',
                            'joinByOrgCodeDisabled': 'Ce lien d\'enregistrement n\'est pas valide. Contacte la personne responsable dans ton organisation.',
                            'emailAlreadyTakenTitle': 'E-mail déjà attribué',
                            'emailAlreadyTaken': 'L\'adresse e-mail indiquée est déjà utilisée.<br><br>Si tu es déjà enregistré, tu peux te connecter avec ton e-mail et ton mot de passe sur <a href="https://learn.micromate.ai">learn.micromate.ai</a>.',
                            'domainValidationFailedTitle': 'E-mail non valide',
                            'domainValidationFailed': 'Pour l\'enregistrement, seul un e-mail de l\'organisation {{organizationName}} est autorisé.'
                        },
                        'selfregistrationwithoutorganisation': {
                            'title': 'Enregistrement {{appName}}',
                            'emailAlreadyTakenTitle': 'E-mail déjà attribué',
                            'emailAlreadyTaken': 'L\'adresse e-mail indiquée est déjà utilisée.<br><br>Si tu es déjà enregistré, tu peux te connecter avec ton e-mail et ton mot de passe sur <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        }
                    },
                    'organization': 'Organisation',
                    'email': 'E-mail',
                    'emailRequiredError': 'L\'e-mail doit être spécifié',
                    'emailInvalidError': 'E-mail non valide',
                    'firstName': 'Prénom',
                    'firstNameRequiredError': 'Le prénom doit être spécifié',
                    'lastName': 'Nom de famille',
                    'lastNameRequiredError': 'Le nom de famille doit être spécifié',
                    'language': 'Langue',
                    'password': 'Mot de passe',
                    'passwordInvalidError': 'Format de mot de passe non valide',
                    'passwordComplexityPolicyMinLength': 'Doit avoir une longueur minimale de {{minLength}}',
                    'passwordComplexityPolicyHasNumber': 'Doit contenir un nombre',
                    'passwordComplexityPolicyHasLowercase': 'Doit contenir une lettre minuscule',
                    'passwordComplexityPolicyHasSymbol': 'Doit contenir un symbole (par exemple, $%*!?)',
                    'passwordComplexityPolicyHasUppercase': 'Doit contenir une lettre majuscule',
                    'confirmPassword': 'Mot de passe (répéter)',
                    'confirmPasswordInvalidError': 'Le mot de passe ne correspond pas',
                    'agbLinkTitle': 'Conditions générales',
                    'dataPrivacyLinkTitle': 'Politique de confidentialité',
                    'dataAccept': 'acceptent',
                    'registrationButton': 'Register',
                    'registrationFailedErrorTitle': 'Échec de l\'enregistrement',
                    'registrationFailedError': 'L\'enregistrement n\'a pas pu être effectué. Veuillez contacter',
                    'invalidCodeError': 'Cette invitation n\'est plus valable. Si vous avez besoin d\'une nouvelle invitation, veuillez contacter votre représentant {{appName}}.',
                    'exchangeCodeError': 'L\'inscription est actuellement impossible. Veuillez contacter',
                    'parameterErrorTitle': 'Paramètres erronés',
                    'parameterError': 'Les paramètres indiqués ne sont pas valables. Contacte la personne responsable dans ton organisation.',
                    'confirmation': {
                        'title': 'Envoi d\'un e-mail de vérification',
                        'description': 'Nous t\'avons envoyé un e-mail de confirmation de ton adresse e-mail à <strong>{{email}}</strong>. Clique sur le lien dans l\'e-mail pour terminer l\'enregistrement. Vérifie ton dossier spam si tu n\'as pas reçu d\'e-mail.'
                    }
                },
                'feedback': {
                    'buttonTitle': 'Donner un feedback',
                    'title': 'Rédige ton commentaire',
                    'sendButton': 'Envoyer',
                    'infoBox': 'Ton nom et ton e-mail sont transmis pour la correspondance.',
                    'resultTitle': 'Merci pour tes commentaires',
                    'resultSuccessMessage': 'Ton commentaire a été transmis à la personne responsable.',
                    'resultErrorMessage': 'Le feedback n\'a pas pu être soumis.',
                    'resultNotAllowFeedbackErrorMessage': 'La fonction de feedback n\'est plus disponible pour cette question ou ce dossier pédagogique.',
                    'resultCloseButton': 'Fermer',
                    'mainTopicTitle': 'Sur quoi veux-tu donner un feedback ?',
                    'topicQuestionButton': 'Question d\'actualité',
                    'topicLearningPackageButton': 'Dossier pédagogique actuel',
                    'topicTitle': 'Que veux-tu signaler ?',
                    'questionFaultyTopic': 'La tâche est incorrecte',
                    'questionUnintelligibleTopic': 'La tâche est incompréhensible',
                    'questionSpellingTopic': 'Cette tâche contient des fautes d\'orthographe',
                    'questionIdeaTopic': 'J\'ai une idée ou une remarque',
                    'learningPackageQuestionSuggestionTopic': 'Proposer une question concrète',
                    'learningPackageTopicSuggestionTopic': 'Proposer un nouveau thème de contenu',
                    'learningPackageFeedbackTopic': 'Donner un feedback sur le kit d\'apprentissage',
                    'questionPreviewHide': 'Masquer la question',
                    'questionPreviewShow': 'Afficher la question'
                },
                'popup': {
                    'wizardBackButton': 'Retour'
                },
                'questionPreview': {
                    'questionTitle': 'Demander',
                    'answerTitle': 'Répondre',
                    'answerFalse': 'Faux',
                    'answerTrue': 'Vrai',
                    'explanationTitle': 'Explication',
                    'sourceTitle': 'Source'
                },
                'slack': {
                    'success': {
                        'title': 'Installation réussie',
                        'description': 'L\'application Taskbase a été installée avec succès et peut maintenant être utilisée.',
                        'button': 'Ouvrir l\'application Taskbase'
                    },
                    'failure': {
                        'title': 'Échec de l\'installation',
                        'description': 'Malheureusement, l\'installation de l\'application Taskbase n\'a pas pu être terminée. Veuillez vérifier l\'identifiant de l\'espace de travail Slack dans Manage Micromate et réinstaller l\'application Taskbase. Pour plus d\'informations, veuillez consulter notre documentation d\'aide.',
                        'button': 'Obtenir de l\'aide'
                    }
                }
            }

        },
        {
            language: 'it',
            translations: {
                'CHARACTER_INDICATOR': '{{length}} / {{maxLength}} segni',
                'NO_CONNECTION_ERROR': 'Problema di connessione: tentativo di connessione al server...',
                'ITEM_LIST_CHOOSE': 'Selezionare',
                'PLACEHOLDER': 'Inserisci messaggio...',
                'ATTACHMENT_ADD': 'Allega file',
                'ATTACHMENT_REMOVE': 'Rimuovi allegato',
                'ATTACHMENT_INVALID': 'File non supportato o troppo grande',
                'THINKING_INDICATOR': 'Sto pensando...',
                'NEW_MESSAGES': 'Nuovi messaggi',
                'FILE_UPLOAD': {
                    'TITLE_ERROR_MULTIPLE': 'Impossibile caricare i file:',
                    'TITLE_ERROR_SINGLE': 'Impossibile caricare i file:',
                    'FILE_ERROR_SIZE': 'File troppo grande',
                    'FILE_ERROR_TYPE': 'Formato file errato',
                    'FILE_ERROR_UNKNOWN': 'Errore sconosciuto'
                },
                'AUTOCOMPLETE': {
                    'HINT_NO_ENTRIES': 'Nessun risultato trovato'
                },
                'profileLearnerMissing': {
                    'title': 'Profilo non ancora pronto',
                    'description': 'Il tuo profilo non è ancora stato trovato. Avvia prima una chat in questa app, poi riprova.',
                    'retry': 'Riprova'
                },
                'resourceViewer': {
                    'notAvailableError': {
                        'title': 'Risorsa non disponibile',
                        'description': 'Spiacente, la risorsa richiesta non è disponibile.'
                    }
                },
                'badge': {
                    'locked': 'Bloccato',
                    'level': 'Livello',
                    'noLevel': 'Raggiunto',
                    'noData': 'La panoramica dei badge è disattivata per questa organizzazione'
                },
                'learningGoal': {
                    'newbie': 'Principiante',
                    'beginners': 'Inesperto',
                    'advanced': 'Avanzato',
                    'skilled': 'Competente',
                    'professional': 'Professionista',
                    'expert': 'Esperto',
                    'noData': 'Nessun pacchetto di apprendimento sbloccato',
                    'noIntroduction': 'Nessuna descrizione disponibile',
                    'feedback': 'Dare un feedback',
                    'releaseDate': 'Ultimo aggiornamento:',
                    'ownerOrganization': 'Creato da',
                    'checkSticker': 'Obiettivo di apprendimento raggiunto',
                    'forgettingApplied': 'Tempo per imparare',
                    'navLearningProgress': 'Obiettivo di apprendimento e progresso',
                    'navQuizzesOverview': 'Visualizzazione del progresso formativo',
                    'navLearningPackageInfo': {
                        'learningPackage': 'Pacchetto di apprendimento Insights',
                        'managedTutor': 'Tutor Insights'
                    },
                    'learningGoal': 'Obiettivo di apprendimento',
                    'numberOfQuestion': 'Avanzamento di {{numberOfQuestion}} domande',
                    'unansweredQuestions': 'di cui {{neverAnsweredQuestions}} senza risposta',
                    'unansweredAndInactiveQuestions': 'di cui {{neverAnsweredQuestions}} senza risposta e {{numberOfInactiveQuestions}} inattive',
                    'inactiveQuestions': 'di cui {{numberOfInactiveQuestions}} inattive',
                    'learnButton': {
                        'learningPackage': 'Scopri questo pacchetto di apprendimento',
                        'managedTutor': 'Avvia questo tutor'
                    },
                    'inactive': 'inattivo',
                    'learningHistoryCopyToast': 'Cronologia di apprendimento copiata',
                    'insightsPopup': {
                        'learningProgress': 'Progresso di apprendimento',
                        'forgetting': 'Dimenticanza',
                        'details': {
                            'tabTitle': 'Dettagli',
                            'learningTitle': 'Inizia a imparare',
                            'feedback': 'Invia feedback',
                            'learning': {
                                'learningPackage': 'Impara il pacchetto',
                                'managedTutor': 'Inizio tutor'
                            },
                            'buttonTitle': 'Dare un feedback',
                            'availableLanguages': 'Lingue disponibili',
                            'noIntroduction': 'Nessuna descrizione disponibile',
                            'releaseDate': 'Ultimo aggiornamento'
                        },
                        'feedback': {
                            'tabTitle': 'Feedback',
                            'clearCurrentFeedbackButton': 'Scartare il feedback',
                            'startNewFeedbackButton': 'Avvia un nuovo feedback'
                        },
                        'questions': {
                            'tabTitle': 'Mappa domande',
                            'deactivatedQuestionLegend': 'Domanda inattiva',
                            'notAnsweredQuestionLegend': 'Domanda senza risposta',
                            'numberOfQuestions': 'Questo pacchetto di apprendimento <strong>contiene {{numberOfQuestion}} domanda</strong>.',
                            'allQuestionsAnswered': ', alle quali hai già <strong>risposto almeno una volta</strong>. ',
                            'noAnsweredQuestions': '. Non hai ancora <strong>risposto a nessuna domanda</strong>. ',
                            'notShowedYetQuestions': '. Ci sono <strong>{{neverAnsweredQuestions}} domande che non ti sono ancora state mostrate</strong>. ',
                            'inactiveQuestions': 'Inoltre, <strong>{{numberOfInactiveQuestions}} domande sono inattive</strong>. ',
                            'allInactiveQuestions': 'Sono <strong>tutti inattivi</strong>. ',
                            'colorExplanation': 'Le domande verdi sono risposte correttamente in modo coerente. Le domande rosse indicano risposte errate.',
                            'helpButtonTitle': 'Spiegazione del diagramma'
                        },
                        'topics': {
                            'tabTitle': 'Argomenti',
                            'noData': 'Inizia una conversazione con il tutor per vedere i tuoi progressi!',
                            'colorExplanation': 'Il colore di ogni quadrato mostra la tua padronanza di quell’argomento.',
                            'masteryLegend': 'Padronanza'
                        },
                        'learningProgressHistory': {
                            'tabTitle': 'Progresso formativo',
                            'correctAnswersLabel': 'Risposte corrette',
                            'incorrectAnswersLabel': 'Risposte errate',
                            'answeredQuestions': 'Domande risposte',
                            'dayLabel': 'Giorni',
                            'weekLabel': 'Settimane',
                            'monthLabel': 'Mesi',
                            'learningProgressLabel': 'Progresso di apprendimento',
                            'learningProgressTooltip': 'Progresso di apprendimento in %',
                            'weekShortVersion': 'Sett',
                            'learningGoalLabel': 'Obiettivo di apprendimento',
                            'forgettingLabel': 'Dimenticanza',
                            'timeOfForgettingLabel': 'Momento della dimenticanza',
                            'nowLabel': 'Adesso',
                            'dailyMode': 'Giorno',
                            'weeklyMode': 'Settimana',
                            'monthlyMode': 'Mese',
                            'helpButtonTitle': 'Spiegazione del diagramma'
                        },
                        'skillsProfile': {
                            'tabTitle': 'Competenze',
                            'noTopicMessage': 'Non è possibile visualizzare un profilo di competenze perché non sono stati definiti argomenti per questo pacchetto di apprendimento.',
                            'learningProgressLabel': 'Progresso di apprendimento in %',
                            'learningProgressTooltip': 'Progresso di apprendimento',
                            'learningGoalLabel': 'Obiettivo di apprendimento',
                            'numberOfQuestionsLabel': 'Numero di domande',
                            'questionsWithoutTopic': 'Domande senza argomento',
                            'helpButtonTitle': 'Spiegazione del diagramma'
                        }
                    }
                },
                'ranking': {
                    'allTime': 'sempre',
                    'points': 'punti',
                    'noOrganizationRanking': 'Non sono disponibili dati per questo periodo.',
                    'noRankingData': 'Nessuna classifica disponibile',
                    'currentMonth': 'mese corrente',
                    'lastMonth': 'mese scorso',
                    'currentYear': 'anno corrente',
                    'lastYear': 'anno scorso',
                    'noData': 'La classifica è disattivata per questa organizzazione'
                },
                'profile': {
                    'email': 'E-mail',
                    'emailAndIdentifier': 'Utente / E-mail',
                    'identifier': 'Utente',
                    'name': 'Nome',
                    'organizationName': 'Organizzazione',
                    'close': 'Vicino',
                    'title': 'Profilo',
                    'noOrganization': 'Nessuna organizzazione',
                    'usePseudonym': 'Mostra pseudonimo',
                    'activePseudonym': 'Si',
                    'inactivePseudonym': 'No',
                    'pseudonym': 'Pseudonimo',
                    'cancel': 'Scartare',
                    'save': 'Salva',
                    'editButton': 'modifica',
                    'monthlyReport': 'Relazione mensile sui progressi compiuti',
                    'monthlyReportSaved': 'Impostazioni salvate',
                    'locale': 'Lingua:',
                    'mainChannelLabel': 'Canale principale',
                    'otherChannels': 'Altri canali',
                    'msTeamsChannel': 'Microsoft Teams',
                    'msTeamsMicromateChannel': 'Microsoft Teams (Micromate)',
                    'msTeamsTaskbaseChannel': 'Microsoft Teams (Taskbase)',
                    'slackChannel': 'Slack',
                    'googleChatChannel': 'Google Chat',
                    'learnChannel': 'Learn Micromate (Web)',
                    'moodleChannel': 'Moodle',
                    'learningReminder': 'Promemoria di apprendimento',
                    'learningReminderOn': 'Attivo',
                    'learningReminderOff': 'Inattivo',
                    'learningReminderFrequency': 'Frequenza',
                    'learningReminderFrequency_weekly': 'Settimanale',
                    'learningReminderFrequency_biweekly': 'Bisettimanale',
                    'learningReminderFrequency_monthly': 'Mensile'
                },
                'moodle-widget-badge': {
                    'wrong-configuration': '{{appName}} non è configurato correttamente e quindi non può essere utilizzato.',
                    'clickToStartSession': 'Avviare una nuova sessione di apprendimento...',
                    'title': 'Imparare  <strong class="micromate-link-title-package-name">{{learningPackageName}}</strong> con {{appName}}',
                    'micromateWillContactSoon': '{{tutorName}} vi contatterà a breve...',
                    'couldNotStartMicromate': 'Non è stato possibile avviare i pacchetti didattici.',
                    'waitingForConnection': 'In attesa del collegamento con {{tutorName}}...'
                },
                'navigation': {
                    'chat': 'Chat',
                    'badges': 'Riconoscimenti',
                    'studyProgress': 'Progresso formativo',
                    'scoreboard': 'Classifica',
                    'profile': 'Profilo'
                },
                'general': {
                    'alreadyConnected': '{{appName}} è già aperto in un\'altra finestra. Chiuderla e ricaricare la pagina.',
                    'autherror': 'Qualcosa è andato storto durante la registrazione con {{appName}}. Si prega di riprovare. Se il problema persiste, contattateci all\'indirizzo {{supportMail}}.',
                    'autherrorButton': 'Provare ad accedere di nuovo',
                    'missingRole': 'Non si dispone delle autorizzazioni necessarie. Contattate il vostro rappresentante {{appName}}.',
                    'missingRoleButton': 'Disconnettersi',
                    'useMicromateHere': 'Utilizzate {{appName}} qui',
                    'endOfHistoryMessage': 'Nessun altro messaggio disponibile.'
                },
                'shared': {
                    'locals': {
                        'af': 'Afrikaans',
                        'sq': 'Albanese',
                        'eu': 'Basco',
                        'bs': 'Bosniaco',
                        'ca': 'Catalano',
                        'hr': 'Croato',
                        'cs': 'Ceco',
                        'da': 'Danese',
                        'nl-nl': 'Olandese (Paesi Bassi)',
                        'nl-be': 'Olandese (Belgio)',
                        'en-us': 'Inglese (USA)',
                        'en-gb': 'Inglese (Regno Unito)',
                        'en-ca': 'Inglese (Canada)',
                        'en-au': 'Inglese (Australia)',
                        'en-in': 'Inglese (India)',
                        'en-sg': 'Inglese (Singapore)',
                        'en-nz': 'Inglese (Nuova Zelanda)',
                        'en-za': 'Inglese (Sudafrica)',
                        'en-ie': 'Inglese (Irlanda)',
                        'en-ph': 'Inglese (Filippine)',
                        'et': 'Estone',
                        'fil': 'Filippino',
                        'fi': 'Finlandese',
                        'fr-fr': 'Francese (Francia)',
                        'fr-ca': 'Francese (Canada)',
                        'fr-be': 'Francese (Belgio)',
                        'fr-ch': 'Francese (Svizzera)',
                        'fr-lu': 'Francese (Lussemburgo)',
                        'fr-mc': 'Francese (Monaco)',
                        'gl': 'Galiziano',
                        'de-at': 'Tedesco (Austria)',
                        'de-ch': 'Tedesco (Svizzera)',
                        'de-de': 'Tedesco (Germania)',
                        'de-lu': 'Tedesco (Lussemburgo)',
                        'de-li': 'Tedesco (Liechtenstein)',
                        'hu': 'Ungherese',
                        'is': 'Islandese',
                        'id': 'Indonesiano',
                        'ga': 'Irlandese',
                        'it': 'Italiano',
                        'lv': 'Lettone',
                        'lt': 'Lituano',
                        'lb': 'Lussemburghese',
                        'mt': 'Maltese',
                        'nb': 'Norvegese Bokmål',
                        'pl': 'Polacco',
                        'pt-br': 'Portoghese (Brasile)',
                        'pt-pt': 'Portoghese (Portogallo)',
                        'ro': 'Rumeno',
                        'gd': 'Gaelico Scozzese',
                        'sr': 'Serbo',
                        'sk': 'Slovacco',
                        'sl': 'Sloveno',
                        'es-es': 'Spagnolo (Spagna)',
                        'es-mx': 'Spagnolo (Messico)',
                        'es-us': 'Spagnolo (USA)',
                        'es-co': 'Spagnolo (Colombia)',
                        'es-ar': 'Spagnolo (Argentina)',
                        'es-ve': 'Spagnolo (Venezuela)',
                        'es-cl': 'Spagnolo (Cile)',
                        'es-pe': 'Spagnolo (Perù)',
                        'es-ec': 'Spagnolo (Ecuador)',
                        'es-pr': 'Spagnolo (Porto Rico)',
                        'sv': 'Svedese',
                        'tr': 'Turco',
                        'vi': 'Vietnamita',
                        'cy': 'Gallese'
                    }
                },
                'registration': {
                    'flows': {
                        'invitation': {
                            'title': 'Completare la registrazione di {{appName}}',
                            'alreadyInitializedTitle': 'Invito non valido',
                            'alreadyInitialized': 'Questo invito non è più valido. Contattate la persona responsabile della vostra organizzazione.<br><br>Se siete già registrati, potete accedere con il vostro indirizzo e-mail e la vostra password all\'indirizzo <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        },
                        'selfregistrationwithorganisation': {
                            'title': 'Registrazione {{appName}}',
                            'invalidOrgCodeTitle': 'Link di registrazione non valido',
                            'invalidOrgCode': 'Questo link di registrazione non è valido. Contattare la persona responsabile della propria organizzazione.',
                            'joinByOrgCodeDisabledTitle': 'Link di registrazione non valido',
                            'joinByOrgCodeDisabled': 'Questo link di registrazione non è valido. Contattare la persona responsabile della propria organizzazione.',
                            'emailAlreadyTakenTitle': 'E-mail già presa',
                            'emailAlreadyTaken': 'L\'indirizzo e-mail inserito è già occupato.<br><br>Se siete già registrati, potete accedere con il vostro indirizzo e-mail e la vostra password all\'indirizzo <a href="https://learn.micromate.ai">learn.micromate.ai</a>.',
                            'domainValidationFailedTitle': 'E-mail non valida',
                            'domainValidationFailed': 'Per la registrazione è consentita solo un\'e-mail dell\'organizzazione {{organizationName}}.'
                        },
                        'selfregistrationwithoutorganisation': {
                            'title': 'Registrazione {{appName}}',
                            'emailAlreadyTakenTitle': 'E-mail già presa',
                            'emailAlreadyTaken': 'L\'indirizzo e-mail inserito è già occupato.<br><br>Se siete già registrati, potete accedere con il vostro indirizzo e-mail e la vostra password all\'indirizzo <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        }
                    },
                    'organization': 'Organizzazione',
                    'email': 'E-mail',
                    'emailRequiredError': 'L\'e-mail deve essere specificato',
                    'emailInvalidError': 'E-mail non valida',
                    'firstName': 'Nome',
                    'firstNameRequiredError': 'Il nome deve essere specificato',
                    'lastName': 'Cognome',
                    'lastNameRequiredError': 'Il cognome deve essere specificato',
                    'language': 'Lingua',
                    'password': 'Password',
                    'passwordInvalidError': 'Formato password non valido',
                    'passwordComplexityPolicyMinLength': 'Deve avere una lunghezza minima di {{minLength}}',
                    'passwordComplexityPolicyHasNumber': 'Deve contenere un numero',
                    'passwordComplexityPolicyHasLowercase': 'Deve contenere una lettera minuscola',
                    'passwordComplexityPolicyHasSymbol': 'Deve contenere un simbolo (ad esempio $%*!?)',
                    'passwordComplexityPolicyHasUppercase': 'Deve contenere una lettera maiuscola',
                    'confirmPassword': 'Password (ripetuta)',
                    'confirmPasswordInvalidError': 'La password non corrisponde',
                    'agbLinkTitle': 'Termini e condizioni generali',
                    'dataPrivacyLinkTitle': 'Informativa sulla privacy',
                    'dataAccept': 'accettare',
                    'registrationButton': 'Registrati',
                    'invalidCodeError': 'Questo invito non è più valido. Se avete bisogno di un nuovo invito, contattate il vostro rappresentante {{appName}}.',
                    'exchangeCodeError': 'La registrazione non è attualmente possibile. Si prega di contattare',
                    'registrationFailedErrorTitle': 'Registrazione fallita',
                    'registrationFailedError': 'Non è stato possibile completare la registrazione. Si prega di contattare',
                    'parameterErrorTitle': 'Parametri errati',
                    'parameterError': 'I parametri specificati non sono validi. Contattare il responsabile della propria organizzazione.',
                    'confirmation': {
                        'title': 'Mail di verifica inviata',
                        'description': 'Le abbiamo inviato un\'e-mail all\'indirizzo <strong>{{email}}</strong> per confermare il suo indirizzo e-mail. Fare clic sul link contenuto nell\'e-mail per completare la registrazione. Controllare la cartella spam se non si è ricevuta l\'e-mail.'
                    }
                },
                'feedback': {
                    'buttonTitle': 'Dare un feedback',
                    'title': 'Scrivete il vostro feedback',
                    'sendButton': 'Inviare',
                    'infoBox': 'Il vostro nome e l\'e-mail saranno trasmessi per la corrispondenza.',
                    'resultTitle': 'Grazie per il vostro feedback',
                    'resultSuccessMessage': 'Il vostro feedback è stato inviato alla persona responsabile.',
                    'resultErrorMessage': 'Non è stato possibile inviare il feedback.',
                    'resultNotAllowFeedbackErrorMessage': 'La funzione di feedback non è più disponibile per questa domanda o per questo pacchetto didattico.',
                    'resultCloseButton': 'Chiudere',
                    'mainTopicTitle': 'Su cosa volete dare un feedback?',
                    'topicQuestionButton': 'Domanda attuale',
                    'topicLearningPackageButton': 'Pacchetto didattico attuale',
                    'topicTitle': 'Che cosa vuole riferire?',
                    'questionFaultyTopic': 'Il compito non è corretto',
                    'questionUnintelligibleTopic': 'Il compito è incomprensibile',
                    'questionSpellingTopic': 'Questo compito contiene errori di ortografia',
                    'questionIdeaTopic': 'Ho un\'idea o un commento',
                    'learningPackageQuestionSuggestionTopic': 'Suggerite una domanda specifica',
                    'learningPackageTopicSuggestionTopic': 'Suggerisci un nuovo argomento di contenuto',
                    'learningPackageFeedbackTopic': 'Feedback sul pacchetto didattico',
                    'questionPreviewHide': 'Nascondi la domanda',
                    'questionPreviewShow': 'Mostra domanda'
                },
                'popup': {
                    'wizardBackButton': 'Indietro'
                },
                'questionPreview': {
                    'questionTitle': 'Domanda',
                    'answerTitle': 'Risposte',
                    'answerFalse': 'Falso',
                    'answerTrue': 'Vero',
                    'explanationTitle': 'Spiegazione',
                    'sourceTitle': 'Spiegazione'
                },
                'slack': {
                    'success': {
                        'title': 'Installazione riuscita',
                        'description': 'L\'app Taskbase è stata installata con successo e può essere utilizzata.',
                        'button': 'Apri l\'app Taskbase'
                    },
                    'failure': {
                        'title': 'Installazione fallita',
                        'description': 'Purtroppo, l\'installazione dell\'app Taskbase non è stata completata. Si prega di verificare l\'identificatore dell\'area di lavoro Slack in Manage Micromate e reinstallare l\'app Taskbase. Per ulteriori informazioni, consultare la nostra documentazione di aiuto.',
                        'button': 'Ottieni aiuto'
                    }
                }
            }
        },
        {
            language: 'nl',
            translations: {
                'CHARACTER_INDICATOR': '{{length}} / {{maxLength}} Karakters',
                'NO_CONNECTION_ERROR': 'Verbindingsprobleem: probeert verbinding te maken met de server...',
                'ITEM_LIST_CHOOSE': 'Selecteer',
                'PLACEHOLDER': 'Bericht invoeren...',
                'ATTACHMENT_ADD': 'Bestand bijvoegen',
                'ATTACHMENT_REMOVE': 'Bijlage verwijderen',
                'ATTACHMENT_INVALID': 'Bestand wordt niet ondersteund of is te groot',
                'THINKING_INDICATOR': 'Aan het nadenken...',
                'NEW_MESSAGES': 'Nieuwe berichten',
                'FILE_UPLOAD': {
                    'TITLE_ERROR_MULTIPLE': 'Bestanden konden niet worden geladen:',
                    'TITLE_ERROR_SINGLE': 'Bestand kan niet worden geladen:',
                    'FILE_ERROR_SIZE': 'Bestand te groot',
                    'FILE_ERROR_TYPE': 'Verkeerd bestandsformaat',
                    'FILE_ERROR_UNKNOWN': 'Onbekende fout'
                },
                'AUTOCOMPLETE': {
                    'HINT_NO_ENTRIES': 'Geen zoekresultaten'
                },
                'profileLearnerMissing': {
                    'title': 'Profiel nog niet klaar',
                    'description': 'Je profiel is nog niet gevonden. Start eerst een chat in deze app en probeer het daarna opnieuw.',
                    'retry': 'Opnieuw proberen'
                },
                'resourceViewer': {
                    'notAvailableError': {
                        'title': 'Bron niet beschikbaar',
                        'description': 'Sorry, de gevraagde bron is niet beschikbaar.'
                    }
                },
                'badge': {
                    'locked': 'Vergrendeld',
                    'level': 'Level',
                    'noLevel': 'Bereikt',
                    'noData': 'Het badge-overzicht is uitgeschakeld voor deze organisatie'
                },
                'learningGoal': {
                    'newbie': 'Nieuwkomer',
                    'beginners': 'Beginners',
                    'advanced': 'Deskundige',
                    'skilled': 'Gevorderde',
                    'professional': 'Professional',
                    'expert': 'Expert',
                    'noData': 'Geen ontgrendelde lespakketten',
                    'noIntroduction': 'Geen beschrijving beschikbaar',
                    'feedback': 'Feedback geven',
                    'releaseDate': 'Laatst bijgewerkt:',
                    'ownerOrganization': 'Gemaakt door',
                    'checkSticker': 'Leerdoel bereikt',
                    'forgettingApplied': 'Tijd om te leren',
                    'navLearningProgress': 'Leerdoel en voortgang',
                    'navQuizzesOverview': 'Overzicht van de leervoortgang',
                    'navLearningPackageInfo': {
                        'learningPackage': 'Leerpakket Insights',
                        'managedTutor': 'Tutor Insights'
                    },
                    'learningGoal': 'Leerdoel',
                    'numberOfQuestion': 'Voortgang van de {{numberOfQuestion}} vragen',
                    'unansweredQuestions': 'waarvan {{neverAnsweredQuestions}} onbeantwoord',
                    'unansweredAndInactiveQuestions': 'waarvan {{neverAnsweredQuestions}} onbeantwoord en {{numberOfInactiveQuestions}} inactief',
                    'inactiveQuestions': 'waarvan {{numberOfInactiveQuestions}} inactief',
                    'learnButton': {
                        'learningPackage': 'Leer dit lespakket',
                        'managedTutor': 'Deze tutor starten'
                    },
                    'inactive': 'inactief',
                    'learningHistoryCopyToast': 'Leerhistorie gekopieerd',
                    'insightsPopup': {
                        'learningProgress': 'Leervoortgang',
                        'forgetting': 'Vergeten',
                        'details': {
                            'tabTitle': 'Details',
                            'learningTitle': 'Begin met leren',
                            'feedback': 'Feedback geven',
                            'learning': {
                                'learningPackage': 'Leer leerpakket',
                                'managedTutor': 'Tutor start'
                            },
                            'buttonTitle': 'Feedback geven',
                            'availableLanguages': 'Beschikbare talen',
                            'noIntroduction': 'Geen beschrijving beschikbaar',
                            'releaseDate': 'Laatst bijgewerkt'
                        },
                        'feedback': {
                            'tabTitle': 'Feedback',
                            'clearCurrentFeedbackButton': 'Feedback verwerpen',
                            'startNewFeedbackButton': 'Nieuwe feedback starten'
                        },
                        'questions': {
                            'tabTitle': 'Vragenkaart',
                            'deactivatedQuestionLegend': 'Inactieve vraag',
                            'notAnsweredQuestionLegend': 'Onbeantwoorde vraag',
                            'numberOfQuestions': 'Dit leerpakket <strong>bevat {{numberOfQuestion}} vraag</strong>.',
                            'allQuestionsAnswered': ', die je al <strong>minstens één keer hebt beantwoord</strong>. ',
                            'noAnsweredQuestions': '. Je hebt nog <strong>geen vragen beantwoord</strong>. ',
                            'notShowedYetQuestions': '. Er zijn <strong>{{neverAnsweredQuestions}} vragen die je nog niet hebt gezien</strong>. ',
                            'inactiveQuestions': 'Daarnaast zijn er <strong>{{numberOfInactiveQuestions}} vragen inactief</strong>. ',
                            'allInactiveQuestions': 'Ze zijn <strong>allemaal inactief</strong>. ',
                            'colorExplanation': 'Groene vragen beantwoord je consequent correct. Rode vragen geven aan dat je nog fouten maakt.',
                            'helpButtonTitle': 'Uitleg van het diagram'
                        },
                        'topics': {
                            'tabTitle': 'Onderwerpen',
                            'noData': 'Start een gesprek met de tutor om je voortgang te zien!',
                            'colorExplanation': 'De kleur van elk vierkant toont je beheersing van dat onderwerp.',
                            'masteryLegend': 'Kennisniveau'
                        },
                        'learningProgressHistory': {
                            'tabTitle': 'Leervoortgang',
                            'correctAnswersLabel': 'Juiste antwoorden',
                            'incorrectAnswersLabel': 'Foute antwoorden',
                            'answeredQuestions': 'Beantwoorde vragen',
                            'dayLabel': 'Dagen',
                            'weekLabel': 'Weken',
                            'monthLabel': 'Maanden',
                            'learningProgressLabel': 'Leervoortgang',
                            'learningProgressTooltip': 'Leervoortgang in %',
                            'weekShortVersion': 'Wk',
                            'learningGoalLabel': 'Leerdoel',
                            'forgettingLabel': 'Vergeten',
                            'timeOfForgettingLabel': 'Moment van vergeten',
                            'nowLabel': 'Nu',
                            'dailyMode': 'Dag',
                            'weeklyMode': 'Week',
                            'monthlyMode': 'Maand',
                            'helpButtonTitle': 'Uitleg van het diagram'
                        },
                        'skillsProfile': {
                            'tabTitle': 'Vaardigheden',
                            'noTopicMessage': 'Er kan geen vaardighedenprofiel worden weergegeven, omdat er geen onderwerpen zijn gedefinieerd voor dit leerpakket.',
                            'learningProgressLabel': 'Leervoortgang in %',
                            'learningProgressTooltip': 'Leervoortgang',
                            'learningGoalLabel': 'Leerdoel',
                            'numberOfQuestionsLabel': 'Aantal vragen',
                            'questionsWithoutTopic': 'Vragen zonder onderwerp',
                            'helpButtonTitle': 'Uitleg van het diagram'
                        }
                    }
                },
                'ranking': {
                    'allTime': 'allTime',
                    'points': 'pt',
                    'noOrganizationRanking': 'Voor deze periode zijn geen gegevens beschikbaar.',
                    'noRankingData': 'Geen ranglijst beschikbaar',
                    'currentMonth': 'deze maand',
                    'lastMonth': 'vorige maand',
                    'currentYear': 'dit jaar',
                    'lastYear': 'vorig jaar',
                    'noData': 'De ranglijst is uitgeschakeld voor deze organisatie'
                },
                'profile': {
                    'email': 'E-mail',
                    'emailAndIdentifier': 'Gebruiker / E-mail',
                    'identifier': 'Gebruiker',
                    'name': 'Naam',
                    'organizationName': 'Organisatie',
                    'close': 'Dichtbij',
                    'title': 'Profiel',
                    'noOrganization': 'Geen organisatie',
                    'usePseudonym': 'Pseudoniem tonen',
                    'activePseudonym': 'Ja',
                    'inactivePseudonym': 'Nee',
                    'pseudonym': 'Pseudoniem',
                    'cancel': 'Gooi weg',
                    'save': 'Opslaan',
                    'editButton': 'bewerken',
                    'monthlyReport': 'Maandelijks voortgangsverslag',
                    'monthlyReportSaved': 'Instellingen opgeslagen',
                    'locale': 'Taal:',
                    'mainChannelLabel': 'Hoofd kanaal',
                    'otherChannels': 'Andere kanalen',
                    'msTeamsChannel': 'Microsoft Teams',
                    'msTeamsMicromateChannel': 'Microsoft Teams (Micromate)',
                    'msTeamsTaskbaseChannel': 'Microsoft Teams (Taskbase)',
                    'slackChannel': 'Slack',
                    'googleChatChannel': 'Google Chat',
                    'learnChannel': 'Learn Micromate (Web)',
                    'moodleChannel': 'Moodle',
                    'learningReminder': 'Leerherinnering',
                    'learningReminderOn': 'Actief',
                    'learningReminderOff': 'Inactief',
                    'learningReminderFrequency': 'Frequentie',
                    'learningReminderFrequency_weekly': 'Wekelijks',
                    'learningReminderFrequency_biweekly': 'Tweewekelijks',
                    'learningReminderFrequency_monthly': 'Maandelijks'
                },
                'moodle-widget-badge': {
                    'wrong-configuration': '{{appName}} is niet correct geconfigureerd en kan daarom niet worden gebruikt.',
                    'clickToStartSession': 'Een nieuwe leersessie starten...',
                    'title': 'Leren <strong class="micromate-link-title-package-name">{{learningPackageName}}</strong> met {{appName}}',
                    'micromateWillContactSoon': '{{tutorName}} neemt binnenkort contact met u op...',
                    'couldNotStartMicromate': 'Leerpakketten konden niet worden gestart.',
                    'waitingForConnection': 'Wachten op verbinding met {{tutorName}}...'
                },
                'navigation': {
                    'chat': 'Chat',
                    'badges': 'Badges',
                    'studyProgress': 'Leervoortgang',
                    'scoreboard': 'Ranglijst',
                    'profile': 'Profiel'
                },
                'general': {
                    'alreadyConnected': '{{appName}} is al geopend in een ander venster.',
                    'autherror': 'Er is iets misgegaan bij het registreren bij {{appName}}. Probeer het opnieuw. Als het probleem zich blijft voordoen, neem dan contact met ons op via {{supportMail}}.',
                    'autherrorButton': 'Probeer opnieuw in te loggen',
                    'missingRole': 'U beschikt niet over de vereiste autorisaties. Neem contact op met uw {{appName}}-vertegenwoordiger.',
                    'missingRoleButton': 'Uitloggen',
                    'useMicromateHere': 'Gebruik {{appName}} hier',
                    'endOfHistoryMessage': 'Geen verdere berichten beschikbaar.'
                },
                'shared': {
                    'locals': {
                        'af': 'Afrikaans',
                        'sq': 'Albanees',
                        'eu': 'Baskisch',
                        'bs': 'Bosnisch',
                        'ca': 'Catalaans',
                        'hr': 'Kroatisch',
                        'cs': 'Tsjechisch',
                        'da': 'Deens',
                        'nl-nl': 'Nederlands (Nederland)',
                        'nl-be': 'Nederlands (België)',
                        'en-us': 'Engels (VS)',
                        'en-gb': 'Engels (Verenigd Koninkrijk)',
                        'en-ca': 'Engels (Canada)',
                        'en-au': 'Engels (Australië)',
                        'en-in': 'Engels (India)',
                        'en-sg': 'Engels (Singapore)',
                        'en-nz': 'Engels (Nieuw-Zeeland)',
                        'en-za': 'Engels (Zuid-Afrika)',
                        'en-ie': 'Engels (Ierland)',
                        'en-ph': 'Engels (Filipijnen)',
                        'et': 'Estisch',
                        'fil': 'Filipijns',
                        'fi': 'Fins',
                        'fr-fr': 'Frans (Frankrijk)',
                        'fr-ca': 'Frans (Canada)',
                        'fr-be': 'Frans (België)',
                        'fr-ch': 'Frans (Zwitserland)',
                        'fr-lu': 'Frans (Luxemburg)',
                        'fr-mc': 'Frans (Monaco)',
                        'gl': 'Galicisch',
                        'de-at': 'Duits (Oostenrijk)',
                        'de-ch': 'Duits (Zwitserland)',
                        'de-de': 'Duits (Duitsland)',
                        'de-lu': 'Duits (Luxemburg)',
                        'de-li': 'Duits (Liechtenstein)',
                        'hu': 'Hongaars',
                        'is': 'IJslands',
                        'id': 'Indonesisch',
                        'ga': 'Iers',
                        'it': 'Italiaans',
                        'lv': 'Lets',
                        'lt': 'Litouws',
                        'lb': 'Luxemburgs',
                        'mt': 'Maltees',
                        'nb': 'Noors (Bokmål)',
                        'pl': 'Pools',
                        'pt-br': 'Portugees (Brazilië)',
                        'pt-pt': 'Portugees (Portugal)',
                        'ro': 'Roemeens',
                        'gd': 'Schots-Gaelisch',
                        'sr': 'Servisch',
                        'sk': 'Slowaaks',
                        'sl': 'Sloveens',
                        'es-es': 'Spaans (Spanje)',
                        'es-mx': 'Spaans (Mexico)',
                        'es-us': 'Spaans (VS)',
                        'es-co': 'Spaans (Colombia)',
                        'es-ar': 'Spaans (Argentinië)',
                        'es-ve': 'Spaans (Venezuela)',
                        'es-cl': 'Spaans (Chili)',
                        'es-pe': 'Spaans (Peru)',
                        'es-ec': 'Spaans (Ecuador)',
                        'es-pr': 'Spaans (Puerto Rico)',
                        'sv': 'Zweeds',
                        'tr': 'Turks',
                        'vi': 'Vietnamees',
                        'cy': 'Welsh'
                    }
                },
                'registration': {
                    'flows': {
                        'invitation': {
                            'title': 'Registratie van {{appName}} voltooien',
                            'alreadyInitializedTitle': 'Ongeldige uitnodiging',
                            'alreadyInitialized': 'Deze uitnodiging is niet langer geldig. Neem contact op met de verantwoordelijke in uw organisatie.<br><br>Als u al geregistreerd bent, kunt u inloggen met uw e-mailadres en wachtwoord op <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        },
                        'selfregistrationwithorganisation': {
                            'title': '{{appName}} registratie',
                            'invalidOrgCodeTitle': 'Ongeldige registratielink',
                            'invalidOrgCode': 'Deze registratielink is niet geldig. Neem contact op met de verantwoordelijke persoon in uw organisatie.',
                            'joinByOrgCodeDisabledTitle': 'Ongeldige registratielink',
                            'joinByOrgCodeDisabled': 'Deze registratielink is niet geldig. Neem contact op met de verantwoordelijke persoon in uw organisatie.',
                            'emailAlreadyTakenTitle': 'E-mail al bezet',
                            'emailAlreadyTaken': 'Het e-mailadres dat u hebt opgegeven is al bezet.<br><br>Als u al geregistreerd bent, kunt u inloggen met uw e-mailadres en wachtwoord op <a href="https://learn.micromate.ai">learn.micromate.ai</a>.',
                            'domainValidationFailedTitle': 'E-mail ongeldig',
                            'domainValidationFailed': 'Alleen een e-mail van de organisatie {{organizationName}} is toegestaan voor registratie.'
                        },
                        'selfregistrationwithoutorganisation': {
                            'title': '{{appName}} registratie',
                            'emailAlreadyTakenTitle': 'E-mail al bezet',
                            'emailAlreadyTaken': 'Het e-mailadres dat u hebt opgegeven is al bezet.<br><br>Als u al geregistreerd bent, kunt u inloggen met uw e-mailadres en wachtwoord op <a href="https://learn.micromate.ai">learn.micromate.ai</a>.'
                        }
                    },
                    'organization': 'Organisatie',
                    'email': 'E-mail',
                    'emailRequiredError': 'E-mail moet worden opgegeven',
                    'emailInvalidError': 'E-mail ongeldig',
                    'firstName': 'Voornaam',
                    'firstNameRequiredError': 'Voornaam moet worden opgegeven',
                    'lastName': 'Achternaam',
                    'lastNameRequiredError': 'Achternaam moet worden opgegeven',
                    'language': 'Taal',
                    'password': 'Wachtwoord',
                    'passwordInvalidError': 'Ongeldig wachtwoord formaat',
                    'passwordComplexityPolicyMinLength': 'Moet een minimale lengte hebben van {{minLength}}',
                    'passwordComplexityPolicyHasNumber': 'Moet een getal bevatten',
                    'passwordComplexityPolicyHasLowercase': 'Moet een kleine letter bevatten',
                    'passwordComplexityPolicyHasSymbol': 'Moet een symbool bevatten (bijv. $%*!?)',
                    'passwordComplexityPolicyHasUppercase': 'Moet een hoofdletter bevatten',
                    'confirmPassword': 'Wachtwoord (herhaal',
                    'confirmPasswordInvalidError': 'Wachtwoord komt niet overeen',
                    'agbLinkTitle': 'Algemene voorwaarden',
                    'dataPrivacyLinkTitle': 'Privacybeleid',
                    'dataAccept': 'accepteren',
                    'registrationButton': 'Registreren',
                    'registrationFailedErrorTitle': 'Registratie mislukt',
                    'registrationFailedError': 'Registratie kon niet worden voltooid. Neem contact op met',
                    'invalidCodeError': 'Deze uitnodiging is niet langer geldig. Als u een nieuwe uitnodiging nodig hebt, neem dan contact op met uw {{appName}}-vertegenwoordiger.',
                    'exchangeCodeError': 'Registratie is momenteel niet mogelijk. Neem contact op met',
                    'parameterErrorTitle': 'Foutieve parameters',
                    'parameterError': 'De opgegeven parameters zijn ongeldig. Neem contact op met de verantwoordelijke persoon in uw organisatie.',
                    'confirmation': {
                        'title': 'Verificatiemail verzonden',
                        'description': 'We hebben je een e-mail gestuurd naar <strong>{email}}</strong> om je e-mailadres te bevestigen. Klik op de link in de e-mail om je registratie te voltooien. Controleer je spam-map als je geen e-mail hebt ontvangen.'
                    }
                },
                'feedback': {
                    'buttonTitle': 'Feedback geven',
                    'title': 'Schrijf je feedback',
                    'sendButton': 'Stuur',
                    'infoBox': 'Uw naam en e-mail worden doorgegeven voor correspondentie',
                    'resultTitle': 'Bedankt voor uw feedback',
                    'resultSuccessMessage': 'Je feedback is naar de verantwoordelijke persoon gestuurd.',
                    'resultErrorMessage': 'De feedback kon niet worden ingediend.',
                    'resultNotAllowFeedbackErrorMessage': 'De feedbackfunctie is niet langer beschikbaar voor deze vraag of dit lespakket.',
                    'resultCloseButton': 'Sluit',
                    'mainTopicTitle': 'Waarover wil je feedback geven?',
                    'topicQuestionButton': 'Huidige vraag',
                    'topicLearningPackageButton': 'Huidig leerpakket',
                    'topicTitle': 'Wat wil je rapporteren?',
                    'questionFaultyTopic': 'De taak is onjuist',
                    'questionUnintelligibleTopic': 'De taak is onbegrijpelijk',
                    'questionSpellingTopic': 'Deze opdracht bevat spelfouten',
                    'questionIdeaTopic': 'Ik heb een idee of opmerking',
                    'learningPackageQuestionSuggestionTopic': 'Konkrete Frage vorschlagen',
                    'learningPackageTopicSuggestionTopic': 'Een specifieke vraag stellen',
                    'learningPackageFeedbackTopic': 'Feedback geven op het lespakket',
                    'questionPreviewHide': 'Vraag verbergen',
                    'questionPreviewShow': 'Toon vraag'
                },
                'popup': {
                    'wizardBackButton': 'Terug'
                },
                'questionPreview': {
                    'questionTitle': 'Vraag',
                    'answerTitle': 'Antwoorden',
                    'answerFalse': 'Niet waar',
                    'answerTrue': 'Waar',
                    'explanationTitle': 'Uitleg',
                    'sourceTitle': 'Bron'
                },
                'slack': {
                    'success': {
                        'title': 'Installatie geslaagd',
                        'description': 'De Taskbase-app is succesvol geïnstalleerd en kan nu worden gebruikt.',
                        'button': 'Taskbase-app openen'
                    },
                    'failure': {
                        'title': 'Installatie mislukt',
                        'description': 'Helaas kon de installatie van de Taskbase-app niet worden voltooid. Controleer de Slack Workspace-identificatie in Manage Micromate en installeer de Taskbase-app opnieuw. Voor meer informatie, raadpleeg onze hulpdocumentatie.',
                        'button': 'Hulp krijgen'
                    }
                }
            }
        }];

    constructor(private translateService: TranslateService) {
    }

    public loadTranslations(): void {
        for (const language of this.translations) {
            this.translateService.setTranslation(language.language, language.translations, true);
        }
    }
}
